import { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { PaginationControls, FilterTabs, TABLE_PAGE_SIZE_OPTIONS } from './ListControls';
import { CLIENT_DDF_AUCTIONS } from '../data/mockAdapter';

// ─── Types ──────────────────────────────────────────────────────────────────────

type AuctionStatus = 'active' | 'ending-soon' | 'ended';
type AuctionFilter = 'all' | AuctionStatus;
type TokenFilter = 'all' | string;

interface DdfAuction {
  id: string;
  facilityName: string;
  accountId: string;
  token: string;
  tokenFullName: string;
  tokenColor: string;
  collateralAmount: number;
  collateralValue: number;
  debtAmount: number;
  reservePrice: number;
  currentBid: number;
  timeRemaining: string;
  status: AuctionStatus;
  bidCount: number;
  kycRequired: boolean;
  eligible: boolean;
}

const AUCTIONS: DdfAuction[] = CLIENT_DDF_AUCTIONS.map((auction) => ({
  id: auction.id,
  facilityName: auction.facilityName,
  accountId: auction.accountId,
  token: auction.token,
  tokenFullName: auction.tokenFullName,
  tokenColor: auction.tokenColor,
  collateralAmount: auction.collateralAmount,
  collateralValue: auction.collateralValue,
  debtAmount: auction.debtAmount,
  reservePrice: auction.reservePrice,
  currentBid: auction.currentBid,
  timeRemaining: auction.timeRemaining,
  status: auction.status,
  bidCount: auction.bidCount,
  kycRequired: auction.kycRequired,
  eligible: auction.eligible,
}));

const ALL_TOKENS = [...new Set(AUCTIONS.map((a) => a.token))].sort();

const AUCTION_STATUS_FILTERS: { value: AuctionFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'ending-soon', label: 'Ending Soon' },
  { value: 'ended', label: 'Ended' },
];

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

// ─── Helpers ────────────────────────────────────────────────────────────────────

function fmtCurrency(value: number): string {
  return `$${value.toLocaleString()}`;
}

export type DdfAuctionsBrowseProps = {
  /** Hide standalone page chrome (back link + H1); use inside Liquidations tabs */
  embedded?: boolean;
  /** Unauthenticated preview: bid CTAs run onConnect instead of navigating */
  connectToBid?: boolean;
  onConnect?: () => void;
};

// ─── Component ──────────────────────────────────────────────────────────────────

export function DdfAuctionsBrowse({
  embedded = false,
  connectToBid = false,
  onConnect,
}: DdfAuctionsBrowseProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<AuctionFilter>('all');
  const [tokenFilter, setTokenFilter] = useState<TokenFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);

  // Reset to page 1 when filters change. Comparing prev state during render
  // is the React-recommended pattern for derived state resets.
  const filterKey = `${searchQuery}|${statusFilter}|${tokenFilter}`;
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey);
    setPage(1);
  }

  const statusCounts = useMemo<Record<AuctionFilter, number>>(() => ({
    all: AUCTIONS.length,
    active: AUCTIONS.filter((a) => a.status === 'active').length,
    'ending-soon': AUCTIONS.filter((a) => a.status === 'ending-soon').length,
    ended: AUCTIONS.filter((a) => a.status === 'ended').length,
  }), []);

  const filtered = useMemo(() => {
    let items = [...AUCTIONS];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (a) =>
          a.id.toLowerCase().includes(q) ||
          a.token.toLowerCase().includes(q) ||
          a.tokenFullName.toLowerCase().includes(q) ||
          a.accountId.toLowerCase().includes(q) ||
          a.facilityName.toLowerCase().includes(q),
      );
    }
    if (statusFilter !== 'all') items = items.filter((a) => a.status === statusFilter);
    if (tokenFilter !== 'all') items = items.filter((a) => a.token === tokenFilter);
    return items;
  }, [searchQuery, statusFilter, tokenFilter]);

  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const activeCount = AUCTIONS.filter((a) => a.status === 'active').length;
  const endingSoonCount = AUCTIONS.filter((a) => a.status === 'ending-soon').length;
  const totalCollateral = AUCTIONS.filter((a) => a.status !== 'ended').reduce((s, a) => s + a.collateralValue, 0);
  const eligibleCount = AUCTIONS.filter((a) => a.status !== 'ended' && a.eligible).length;

  function goAuction(auctionId: string) {
    navigate(`/liquidations/auction/${auctionId}`);
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {!embedded && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <Link
              to="/liquidations"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2"
            >
              <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-3 w-3" />
              Back to Liquidations
            </Link>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <Icon name="streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court" className="h-6 w-6" />
              DDF Auctions
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Browse active DDF (Default Distressed Facility) auctions and place bids.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard title="Active Auctions" value={String(activeCount)} icon={<Icon name="streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard title="Ending Soon" value={String(endingSoonCount)} icon={<Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-5 w-5 text-amber-500" />} valueClassName={endingSoonCount > 0 ? 'text-amber-600' : ''} />
        <StatCard title="Total Collateral Value" value={fmtCurrency(totalCollateral)} icon={<Icon name="streamline:money-safe-vault-saving-combo-payment-safe-money-combination-finance" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard title="Eligible to Bid" value={String(eligibleCount)} icon={<Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-5 w-5 text-green-500" />} valueClassName="text-green-600" />
      </div>

      <Card>
        <CardHeader className="p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>All DDF Auctions</CardTitle>
              <CardDescription>{filtered.length} auctions</CardDescription>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <select
                value={tokenFilter}
                onChange={(e) => setTokenFilter(e.target.value)}
                className={`${INPUT_CLASS} w-full sm:w-36`}
              >
                <option value="all">All Tokens</option>
                {ALL_TOKENS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <div className="relative w-full sm:w-72">
                <Icon name="streamline:search-visual" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by ID, token, facility, account..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`${INPUT_CLASS} pl-9`}
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <div className="mb-4">
            <FilterTabs<AuctionFilter>
              filters={AUCTION_STATUS_FILTERS}
              active={statusFilter}
              onChange={setStatusFilter}
              counts={statusCounts}
            />
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              No auctions match your filters.
            </div>
          ) : (
            <>
              <div className="hidden lg:block overflow-x-auto">
                <div className="grid grid-cols-[0.9fr_1.2fr_0.6fr_0.8fr_0.8fr_0.8fr_0.6fr_0.5fr_0.6fr_0.7fr] gap-3 items-center pb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground min-w-[900px]">
                  <span>Auction</span>
                  <span>Facility</span>
                  <span>Token</span>
                  <span className="text-right">Collateral</span>
                  <span className="text-right">Reserve</span>
                  <span className="text-right">Current Bid</span>
                  <span className="text-center">Bids</span>
                  <span className="text-center">Time</span>
                  <span className="text-center">Status</span>
                  <span className="text-center">Action</span>
                </div>
                <div className="divide-y divide-border min-w-[900px]">
                  {paginated.map((auc) => (
                    <div
                      key={auc.id}
                      className="grid grid-cols-[0.9fr_1.2fr_0.6fr_0.8fr_0.8fr_0.8fr_0.6fr_0.5fr_0.6fr_0.7fr] gap-3 items-center py-3 text-sm"
                    >
                      <div>
                        <div className="font-mono font-medium text-xs">{auc.id}</div>
                        <div className="text-xs text-muted-foreground">{auc.accountId}</div>
                      </div>
                      <span className="text-xs truncate" title={auc.facilityName}>
                        {auc.facilityName}
                      </span>
                      <span className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-semibold ${auc.tokenColor}`}>
                        {auc.token}
                      </span>
                      <div className="text-right">
                        <div className="text-xs font-medium">{fmtCurrency(auc.collateralValue)}</div>
                        <div className="text-xs text-muted-foreground">
                          {auc.collateralAmount.toFixed(2)} {auc.token}
                        </div>
                      </div>
                      <span className="text-right text-xs">{fmtCurrency(auc.reservePrice)}</span>
                      <span className="text-right text-xs font-semibold">
                        {auc.currentBid > 0 ? fmtCurrency(auc.currentBid) : 'No bids'}
                      </span>
                      <span className="text-center text-xs">{auc.bidCount}</span>
                      <span className="text-center text-xs">
                        <span
                          className={`inline-flex items-center gap-1 ${auc.status === 'ending-soon' ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`}
                        >
                          <Icon name="streamline:interface-time-stop-watch-timer-countdown-clock" className="h-3 w-3" />
                          {auc.timeRemaining}
                        </span>
                      </span>
                      <div className="flex justify-center">
                        <AuctionStatusBadge status={auc.status} />
                      </div>
                      <div className="flex justify-center">
                        {auc.status === 'ended' ? (
                          <span className="text-xs text-muted-foreground">Settled</span>
                        ) : connectToBid && onConnect ? (
                          <Button onClick={onConnect}>
                            <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="mr-2 h-4 w-4" />
                            Connect to Bid
                          </Button>
                        ) : (
                          <Button onClick={() => goAuction(auc.id)}>Place Bid</Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:hidden space-y-3">
                {paginated.map((auc) => (
                  <div key={auc.id} className="rounded-lg border border-border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium text-sm">{auc.id}</span>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${auc.tokenColor}`}>
                          {auc.token}
                        </span>
                      </div>
                      <AuctionStatusBadge status={auc.status} />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {auc.facilityName} · {auc.accountId}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Collateral</span>
                        <div className="font-medium">{fmtCurrency(auc.collateralValue)}</div>
                        <div className="text-muted-foreground">
                          {auc.collateralAmount.toFixed(2)} {auc.token}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-muted-foreground">Reserve</span>
                        <div className="font-medium">{fmtCurrency(auc.reservePrice)}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Current Bid</span>
                        <div className="font-semibold">{auc.currentBid > 0 ? fmtCurrency(auc.currentBid) : 'No bids'}</div>
                      </div>
                      <div className="text-right">
                        <span className="text-muted-foreground">Time Left</span>
                        <div className={`font-medium ${auc.status === 'ending-soon' ? 'text-amber-600' : ''}`}>
                          {auc.timeRemaining}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-xs text-muted-foreground">
                        {auc.bidCount} bid{auc.bidCount !== 1 ? 's' : ''}
                      </span>
                      {auc.kycRequired && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">KYC Required</span>
                      )}
                      {!auc.eligible && auc.status !== 'ended' && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">Not Eligible</span>
                      )}
                    </div>
                    {auc.status === 'ended' ? (
                      <Button variant="outline" className="w-full" size="sm" disabled>
                        Settled
                      </Button>
                    ) : connectToBid && onConnect ? (
                      <Button variant="outline" className="w-full" size="sm" onClick={onConnect}>
                        <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="mr-2 h-4 w-4" />
                        Connect Wallet to Bid
                      </Button>
                    ) : (
                      <Button className="w-full" size="sm" onClick={() => goAuction(auc.id)}>
                        <Icon name="streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court" className="mr-2 h-4 w-4" />
                        Place Bid
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          <PaginationControls
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalItems={filtered.length}
            label="auctions"
            pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  valueClassName,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className={`mt-2 text-2xl sm:text-3xl font-bold ${valueClassName ?? ''}`}>{value}</div>
    </div>
  );
}

function AuctionStatusBadge({ status }: { status: AuctionStatus }) {
  switch (status) {
    case 'active':
      return <span className="rounded-full px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-700">Active</span>;
    case 'ending-soon':
      return <span className="rounded-full px-2.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">Ending Soon</span>;
    case 'ended':
      return <span className="rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600">Settled</span>;
  }
}
